import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function DiscordCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const err = params.get('error');
    if (err) {
      setStatus('error'); setError('Discord authorisation was cancelled.');
      setTimeout(() => nav('/dashboard'), 2200); return;
    }
    if (!code) {
      setStatus('error'); setError('Missing authorisation code.');
      setTimeout(() => nav('/dashboard'), 2200); return;
    }
    const redirect_uri = `${window.location.origin}/discord/callback`;
    (async () => {
      try {
        await api.post('/discord/exchange', { code, state, redirect_uri });
        await refresh();
        setStatus('linked');
        toast.success('Discord linked — VIP role assigned');
        setTimeout(() => nav('/dashboard'), 1800);
      } catch (e) {
        setStatus('error');
        setError(formatApiError(e.response?.data?.detail) || 'Discord linking failed.');
      }
    })();
  }, [nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white p-6">
      <div className="glass-strong rounded-2xl p-10 max-w-md w-full text-center" data-testid="discord-callback">
        {status === 'processing' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full border-2 border-white/10 border-t-[#5865F2] animate-spin" />
            <div className="mt-6 font-display font-bold text-2xl">Linking your Discord…</div>
            <p className="mt-2 text-sm text-white/60">Assigning your VIP role.</p>
          </>
        )}
        {status === 'linked' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-[#5865F2]/20 flex items-center justify-center text-[#7B85F5]"><Check className="h-6 w-6" /></div>
            <div className="mt-6 font-display font-bold text-2xl">Discord linked</div>
            <p className="mt-2 text-sm text-white/60">Your VIP role is now active in the server.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center text-red-300"><X className="h-6 w-6" /></div>
            <div className="mt-6 font-display font-bold text-2xl">Link failed</div>
            <p className="mt-2 text-sm text-white/60">{error}</p>
          </>
        )}
      </div>
    </div>
  );
}
